// Poll size limits, the same on every plan (BACKLOG #25). Limits are about
// readability on a projector, not money.
export const QUESTION_MAX = 200;
export const ANSWER_MAX = 80;
export const ANSWERS_MIN = 2;
export const ANSWERS_MAX = 20;

// The presenter keeps the big layout up to this many answers, then switches
// to the compact two-column layout that highlights the top 3.
export const BIG_LAYOUT_MAX_ANSWERS = 6;

// Lifecycle (BACKLOG #18, #19). Durations in milliseconds.
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export type Plan = "anonymous" | "free" | "pro";

/**
 * Questions in one poll (question sets, Nav 2026-10-11): one without an account, so the free
 * account has a reason; 10 on a free account; 50 on Pro and Event Pass.
 */
export const QUESTIONS_MAX: Record<Plan, number> = { anonymous: 1, free: 10, pro: 50 };
export const QUESTIONS_MAX_ANY = 50;

/** How long a published poll waits for its first vote before it expires. */
export const WAIT_FOR_FIRST_VOTE_MS = 7 * DAY;

/** The live window, counted from the first vote. Pro and Event Pass run until closed, capped at 1 year. */
export const LIVE_WINDOW_MS: Record<Plan, number> = {
  anonymous: DAY,
  free: 7 * DAY,
  pro: 365 * DAY,
};

/** How long results stay readable after the poll closes. `Infinity` means forever. */
export const RESULTS_KEPT_MS: Record<Plan, number> = {
  anonymous: 7 * DAY,
  free: 365 * DAY,
  pro: Number.POSITIVE_INFINITY,
};

// ---- Big crowds (viral growth plan, Nav 2026-11-03) ----
// A poll without an account runs free up to ANON_VOTERS. Past that the host signs in (free,
// unlimited) to keep new voters coming. A short grace keeps the room going while they do.

/** Voters an anonymous poll takes before the grace period starts. Free accounts have no cap. */
export const ANON_VOTERS = 1000;
/** The host's screens get a heads-up at this many voters on an anonymous poll. */
export const ANON_HEADS_UP_AT = 500;
/** The grace period ends at this long after it starts, or at GRACE_VOTERS total voters, whichever comes first. */
export const GRACE_MS = 30 * 60 * 1000;
export const GRACE_VOTERS = 1500;
/** A locked poll's waiting voters retry within this long of the unlock. */
export const UNLOCK_RETRY_MS = 30 * 1000;
/** Voter counts that flash on the big screen, once each. */
export const PULSE_MILESTONES = [100, 500, 1000, 5000, 10000] as const;
/** A poll passing this many voters emails us (spike radar). */
export const SPIKE_RADAR_AT = 500;
/** An account past this many voters in a calendar month is flagged to us (fair use). Nothing is blocked. */
export const FAIR_USE_MONTHLY_VOTERS = 50000;
